import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { adminNotificationApi, getUnreadCount, type AdminNotification } from "../utility/adminNotificationApi";

const POLL_INTERVAL_MS = 15_000;

export const useAdminNotifications = () => {
  const toast = useToast();
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const knownIds = useRef<Set<number>>(new Set());
  const initialized = useRef(false);

  const load = useCallback(async () => {
    try {
      const response = await adminNotificationApi.list();
      const next = response.data.data;
      const newItems = initialized.current
        ? next.filter((item) => !knownIds.current.has(item.id) && !item.is_read)
        : [];

      setNotifications(next);
      setUnreadCount(getUnreadCount(response.data));
      knownIds.current = new Set(next.map((item) => item.id));
      initialized.current = true;

      if (newItems.length > 0) {
        toast({
          title: "새 감정 접수",
          description: `새 감정 접수 ${newItems.length}건이 들어왔습니다.`,
          status: "info",
          duration: 4500,
          isClosable: true,
          position: "top-right",
        });
      }
    } catch {
      // 알림 조회 실패는 기존 화면 사용을 막지 않는다.
    }
  }, [toast]);

  useEffect(() => {
    void load();
    const handlePushRefresh = () => void load();
    window.addEventListener("quazar-admin-notification-refresh", handlePushRefresh);
    const timer = window.setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("quazar-admin-notification-refresh", handlePushRefresh);
    };
  }, [load]);

  const markAsRead = useCallback(async (id: number) => {
    await adminNotificationApi.markAsRead(id);
    setNotifications((items) => items.map((item) => item.id === id ? { ...item, is_read: true } : item));
    setUnreadCount((count) => Math.max(0, count - 1));
  }, []);

  const markAllAsRead = useCallback(async () => {
    await adminNotificationApi.markAllAsRead();
    setNotifications((items) => items.map((item) => ({ ...item, is_read: true })));
    setUnreadCount(0);
  }, []);

  return { notifications, unreadCount, markAsRead, markAllAsRead };
};
